import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { FileText, Upload, Download, Eye, Trash2, AlertCircle, RefreshCw, Play, Star } from 'lucide-react';
import ConfirmDeleteModal from '../modals/ConfirmationModal';
import B2CInterviewSetupModal from '../modals/B2CInterviewSetupModal';
import api from '../../api/client';
import NotificationPopup from '../admin/component/NotificationPopup';

const getNormalizedCredits = (data) => {
  const payload = data?.results || data || {};
  const primaryCredits = [
    payload.available_credits,
    payload.availableCredits,
    payload.remaining_credits,
    payload.remainingCredits,
    payload.balance,
  ]
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value));

  if (Number.isFinite(primaryCredits)) {
    return primaryCredits;
  }

  const currentBalance = Number(payload.current_balance ?? payload.currentBalance);
  const dailyCredits = Number(payload.daily_credits ?? payload.dailyCredits);

  return (Number.isFinite(currentBalance) ? currentBalance : 0) +
    (Number.isFinite(dailyCredits) ? dailyCredits : 0);
};

const ResumeSection = ({
  resumes,
  setResumes,
  loading,
  error,
  uploadLoading,
  deleteLoading,
  handleResumeUpload,
  handleResumeDelete,
  refreshResumes,
  setError,
  snackbar,
  setSnackbar,
  onUploadClick,
  handleResumeClick,
  formatDate
}) => {
  const { t } = useTranslation();

  const [resumeToDelete, setResumeToDelete] = useState(null);
  const [latestResumeId, setLatestResumeId] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [showAll, setShowAll] = useState(false); // toggles showing all resumes in a scrollable area

  // show only latest N by default
  const DEFAULT_VISIBLE = 2;

  useEffect(() => {
    if (resumes && resumes.length > 0) {
      const sorted = [...resumes].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      setLatestResumeId(sorted[0].id);
    }
  }, [resumes]);

  const confirmDelete = (resumeId) => setResumeToDelete(resumeId);

  const handleConfirmedDelete = async () => {
    if (!resumeToDelete) return;
    try {
      await handleResumeDelete(resumeToDelete);
      if (resumeToDelete === latestResumeId) {
        const remaining = resumes.filter(r => r.id !== resumeToDelete);
        if (remaining.length > 0) {
          const sortedRemaining = [...remaining].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
          setLatestResumeId(sortedRemaining[0].id);
        } else {
          setLatestResumeId(null);
        }
      }
    } catch (err) {
      // handled by hook
    } finally {
      setResumeToDelete(null);
    }
  };

  const clearError = () => setError(null);

  const handleStartInterview = async (resume) => {
    const adminToken = localStorage.getItem('token');
    if (!adminToken) {
      setError(t('myDetails.resumes.loginRequired'));
      return;
    }
    try {
      const creditResponse = await api.get('observability/user-credits/');
      const credits = getNormalizedCredits(creditResponse.data);
      if (credits <= 0) {
        setError(t('myDetails.resumes.noCredits'));
        return;
      }
      setSelectedResume(resume);
      setShowInterviewModal(true);
    } catch (err) {
      setError(t('myDetails.resumes.creditError'));
    }
  };

  const handleInterviewStart = (sessionId, inviteToken = null) => {
    // Determine the base interview URL based on environment
    let baseInterviewUrl;

    if (process.env.REACT_APP_INTERVIEW_UI_URL) {
      // Use environment variable if set (Dev/Prod)
      baseInterviewUrl = process.env.REACT_APP_INTERVIEW_UI_URL;
    } else {
      // Use current origin for local development (localhost:3000 or localhost:3001)
      baseInterviewUrl = window.location.origin;
    }

    const relativePath = inviteToken ? `/interview/invite/${inviteToken}` : `/interview/${sessionId}?startInterview=true`;
    const targetUrl = `${baseInterviewUrl}${relativePath}`;
    window.open(targetUrl, '_blank');
  };

  const handleDownload = (resume) => {
    window.open(resume.file_url, '_blank');
  };

  const handleSetActive = useCallback(async (resumeId) => {
    try {
      // 1. Call API
      await api.post(`/resumes/${resumeId}/set_active/`);

      // 2. Update Local State Immediately (Optimistic UI)
      if (setResumes) {
        setResumes(currentResumes =>
          currentResumes.map(r => ({
            ...r,
            // Set the clicked resume to active, all others to inactive
            is_active: r.id === resumeId
          }))
        );
      } else {
        // Fallback if setResumes isn't available
        await refreshResumes();
      }

      setSnackbar({ open: true, message: t('myDetails.resumes.setActiveSuccess'), severity: 'success' });
    } catch (err) {
      console.error("Set Active Error:", err);
      setError('Failed to set active resume. Please try again.');
    }
  }, [setResumes, refreshResumes, setSnackbar, setError, t]);

  useEffect(() => {
    // Wait until resumes are loaded
    if (resumes && resumes.length > 0) {
      const hasActiveResume = resumes.some(r => r.is_active);

      // If no resume is currently active, set the most recent one as active by default.
      if (!hasActiveResume) {
        const latestResume = [...resumes].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))[0];
        handleSetActive(latestResume.id);
      }
    }
  }, [resumes, handleSetActive]);

  // compute sorted resumes and visible slice (latest first)
  const sortedResumes = resumes ? [...resumes].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)) : [];
  const visibleResumes = showAll ? sortedResumes : sortedResumes.slice(0, DEFAULT_VISIBLE);
  const hasMore = sortedResumes.length > DEFAULT_VISIBLE;

  return (
    <>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <div className="w-full flex flex-col bg-white border border-gray-100/80 shadow-[0_2px_12px_rgba(0,0,0,0.02)] rounded-2xl overflow-hidden min-h-[350px]">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3 bg-white">
          <div className="flex items-center space-x-3">
            <h3 className="text-base font-bold text-gray-900">{t('myDetails.resumes.title')}</h3>
            {(loading || uploadLoading) && (
              <div className="animate-spin">
                <RefreshCw className="w-4 h-4 text-[#6366f1]" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (resumes && resumes.length >= 5) {
                  setError(t('myDetails.resumes.maxResumesError'));
                } else {
                  onUploadClick();
                }
              }}
              className={`inline-flex items-center space-x-1.5 bg-[#6366f1] text-white hover:bg-[#5046e5] px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-all active:scale-95 ${uploadLoading || (resumes && resumes.length >= 5) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={uploadLoading}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>{uploadLoading ? t('myDetails.resumes.uploading') : t('myDetails.resumes.uploadResume')}</span>
            </button>
          </div>
        </div>

        <div className="px-5 pt-3 pb-5 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50/80 backdrop-blur-sm border border-red-100 rounded-xl flex items-start sm:items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div className="flex-1 text-sm text-red-600 font-medium">{error}</div>
              <button onClick={clearError} className="ml-2 text-red-400 hover:text-red-600" aria-label="Dismiss error">×</button>
            </div>
          )}

          {loading && (!resumes || resumes.length === 0) ? (
            <div className="text-center py-12">
              <div className="animate-spin mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500 text-sm">{t('myDetails.resumes.loading')}</p>
            </div>
          ) : (!resumes || resumes.length === 0) ? (
            <div className="text-center py-12 border border-dashed border-gray-150 rounded-2xl p-6">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-1.5 font-medium text-sm">{t('myDetails.resumes.noResumes')}</p>
              <p className="text-gray-400 text-xs">{t('myDetails.resumes.uploadFirst')}</p>
            </div>
          ) : (
            <div className="space-y-4"
              aria-live="polite">
              {visibleResumes.map((resume) => (
                <div
                  key={resume.id}
                  className={`rounded-2xl p-4 transition-all duration-300 border flex flex-col gap-3 group relative ${resume.is_active
                    ? 'bg-white border-indigo-100 shadow-[0_2px_8px_rgba(99,102,241,0.04)]'
                    : 'bg-white border-gray-100 hover:border-gray-200'
                    }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleResumeClick(resume)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleResumeClick(resume); }}
                    >
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center bg-[#eff6ff] text-[#3b82f6]">
                        <FileText className="w-5 h-5" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h4
                          className="text-gray-900 font-bold truncate text-sm leading-snug"
                          title={resume.name}
                          aria-label={resume.name}
                        >
                          {resume.name}
                        </h4>
                        <div className="text-gray-400 text-[10px] mt-0.5 font-semibold">{t('myDetails.resumes.uploadedAt', 'Uploaded {{date}}', { date: formatDate(resume.uploaded_at) })}</div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleStartInterview(resume)}
                      className="flex items-center justify-center bg-[#f0fdf4] text-[#16a34a] border border-[#dcfce7] hover:bg-[#dcfce7] px-3.5 py-1.5 rounded-full transition-all text-[11px] font-bold shadow-sm active:scale-95 flex-shrink-0"
                      title={t('myDetails.resumes.startInterview')}
                      aria-label={`${t('myDetails.resumes.startInterview')} for ${resume.name}`}
                    >
                      <Play className="w-3 h-3 mr-1 fill-current" />
                      <span>{t('myDetails.resumes.startInterview')}</span>
                    </button>
                  </div>

                  {resume.parsed_data?.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {resume.parsed_data.skills.slice(0, 3).map((skill, index) => (
                        <span key={index} className="px-2.5 py-0.5 bg-gray-50 text-gray-500 border border-gray-100 rounded-md text-[10px] font-semibold">
                          {skill.length > 15 ? skill.slice(0, 15) + "..." : skill}
                        </span>
                      ))}
                      {resume.parsed_data.skills.length > 3 && (
                        <span className="text-gray-400 text-[10px] font-semibold self-center">+{resume.parsed_data.skills.length - 3}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2.5 border-t border-gray-50/50 pt-2.5 mt-0.5">
                    {!resume.is_active ? (
                      <button
                        onClick={() => handleSetActive(resume.id)}
                        className="text-gray-400 hover:text-amber-500 transition-all active:scale-95"
                        title={t('myDetails.resumes.setActive')}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="text-amber-500 cursor-default" title={t('myDetails.resumes.activeResume')}>
                        <Star className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" />
                      </div>
                    )}

                    <button
                      onClick={() => handleDownload(resume)}
                      className="text-gray-400 hover:text-[#6366f1] transition-all active:scale-95"
                      title={t('myDetails.resumes.download')}
                    >
                      <Download className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleResumeClick(resume)}
                      className="text-gray-400 hover:text-[#6366f1] transition-all active:scale-95"
                      title={t('myDetails.resumes.viewDetails')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => {
                        if (resume.is_active) {
                          setError(t('myDetails.resumes.deleteActiveError'));
                        } else {
                          confirmDelete(resume.id);
                        }
                      }}
                      disabled={deleteLoading}
                      className="text-gray-400 hover:text-red-500 transition-all active:scale-95 disabled:opacity-50"
                      title={t('myDetails.resumes.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="pt-1">
                  <button
                    onClick={() => setShowAll(prev => !prev)}
                    className="text-xs font-bold text-[#6366f1] hover:underline flex items-center justify-center gap-1 mt-4 pt-4 border-t border-gray-100 w-full"
                    aria-expanded={showAll}
                  >
                    {showAll ? t('myDetails.resumes.showLess') : t('myDetails.resumes.viewAllResumesLink', 'View all resumes →', { count: sortedResumes.length })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal open={Boolean(resumeToDelete)} onClose={() => setResumeToDelete(null)} onConfirm={handleConfirmedDelete} />

      <B2CInterviewSetupModal isOpen={showInterviewModal} onClose={() => setShowInterviewModal(false)} resume={selectedResume} onStartInterview={handleInterviewStart} />
    </>
  );
};

export default ResumeSection;
